import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { fetchPublicDealers } from "../../services/automapDealerPublicApi";
import { searchPublicVehicleListings } from "../../services/publicVehicleListingsService";
import { useClientViewSeo } from "../../clientViewRuntime/hooks/useClientViewSeo";
import { fetchPublicPlacementsFeed } from "../../services/automapFeaturedPlacementApi";
import { extractFeaturedListings } from "../../constants/automapFeaturedPlacementConstants";
import { fetchPublicExpansionCities } from "../../services/automapCityExpansionApi";
import SeoLandingRenderer from "../../components/automap/seo/SeoLandingRenderer";
import { AUTOMAPA_CLIENT_VIEW_SLUG, SEO_PAGE_TYPES } from "../../constants/seoPageConstants";
import { trackSeoPageEvent, SEO_ANALYTICS_EVENTS } from "../../constants/seoAnalyticsConstants";
import {
  buildSeoBreadcrumbs,
  buildSeoInternalLinks,
  buildSeoPageInventoryQuery,
  buildSeoPageMetadata,
  buildSeoStructuredData,
  getSeoFaqItems,
  getSeoPageConfig,
  normalizePath,
  shouldNoindexSeoPage,
} from "../../services/seoPageService";
import "../../styles/seoLanding.css";

export default function SeoLandingPage() {
  const location = useLocation();
  const path = normalizePath(location.pathname);
  const config = getSeoPageConfig(path);

  const [listings, setListings] = useState([]);
  const [featuredListings, setFeaturedListings] = useState([]);
  const [inventoryTotal, setInventoryTotal] = useState(0);
  const [loading, setLoading] = useState(Boolean(config));
  const [inventoryError, setInventoryError] = useState(null);
  const [dealers, setDealers] = useState([]);
  const [dealersLoading, setDealersLoading] = useState(false);
  const [publicCitySlugs, setPublicCitySlugs] = useState(null);

  const origin = typeof window !== "undefined" ? window.location.origin : "";

  const metadata = useMemo(() => {
    if (!config) return null;
    return buildSeoPageMetadata(config, { origin, inventoryCount: inventoryTotal });
  }, [config, origin, inventoryTotal]);

  const breadcrumbs = useMemo(() => (config ? buildSeoBreadcrumbs(config) : []), [config]);
  const internalLinks = useMemo(() => (config ? buildSeoInternalLinks(config) : []), [config]);
  const faqItems = useMemo(() => (config ? getSeoFaqItems(config) : []), [config]);

  const structuredData = useMemo(() => {
    if (!config) return null;
    return buildSeoStructuredData(config, {
      breadcrumbs,
      listings,
      inventoryCount: inventoryTotal,
    });
  }, [config, breadcrumbs, listings, inventoryTotal]);

  useClientViewSeo({
    title: metadata?.metaTitle || "AutoMapa",
    description: metadata?.metaDescription,
    canonicalUrl: metadata?.canonicalUrl,
    robots: metadata?.robots,
    openGraph: metadata?.openGraph,
    twitter: metadata?.twitter,
    structuredData,
    jsonLdId: "automap-seo-jsonld",
  });

  useEffect(() => {
    fetchPublicExpansionCities()
      .then((payload) => setPublicCitySlugs(payload.publicSlugs || []))
      .catch(() => setPublicCitySlugs([]));
  }, []);

  useEffect(() => {
    if (!config) return;
    trackSeoPageEvent(SEO_ANALYTICS_EVENTS.CONTENT_PAGE_VIEWED, {
      path: config.path,
      pageType: config.pageType,
    });
  }, [config]);

  useEffect(() => {
    if (!config || inventoryTotal > 0 || loading) return;
    trackSeoPageEvent(SEO_ANALYTICS_EVENTS.PAGE_NO_INVENTORY_VIEWED, {
      path: config.path,
      pageType: config.pageType,
    });
  }, [config, inventoryTotal, loading]);

  useEffect(() => {
    if (!config) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setInventoryError(null);

    const query = buildSeoPageInventoryQuery(config);

    searchPublicVehicleListings(AUTOMAPA_CLIENT_VIEW_SLUG, query)
      .then((result) => {
        if (cancelled) return;
        if (!result.ok) {
          setInventoryError("fetch_failed");
          setListings([]);
          setInventoryTotal(0);
          return;
        }
        setListings(result.listings);
        setInventoryTotal(result.pagination?.total ?? result.listings.length);
      })
      .catch(() => {
        if (!cancelled) setInventoryError("fetch_failed");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    fetchPublicPlacementsFeed({
      pageType: "seo",
      pagePath: config.path,
      city: config.city,
      slotKeys: "seo_page_top",
      bodyType: config.bodyType,
    })
      .then((payload) => {
        if (!cancelled) {
          setFeaturedListings(extractFeaturedListings(payload.feed, "seo_page_top"));
        }
      })
      .catch(() => {
        if (!cancelled) setFeaturedListings([]);
      });

    return () => {
      cancelled = true;
    };
  }, [config]);

  useEffect(() => {
    if (!config || config.pageType !== SEO_PAGE_TYPES.DEALER_CITY) return;

    let cancelled = false;
    setDealersLoading(true);

    fetchPublicDealers({ city: config.city })
      .then((data) => {
        if (!cancelled) setDealers(data.dealers || []);
      })
      .catch(() => {
        if (!cancelled) setDealers([]);
      })
      .finally(() => {
        if (!cancelled) setDealersLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [config]);

  if (!config) {
    return (
      <main className="seo-landing seo-landing--not-found">
        <h1>Página no encontrada</h1>
        <p>Esta landing SEO no está registrada en AutoMapa.</p>
        <Link to="/v/automap">Volver al inicio</Link>
      </main>
    );
  }

  if (
    config.citySlug &&
    publicCitySlugs &&
    publicCitySlugs.length > 0 &&
    !publicCitySlugs.includes(config.citySlug)
  ) {
    return (
      <main className="seo-landing seo-landing--not-found">
        <h1>Ciudad en preparación</h1>
        <p>Esta ciudad aún no está disponible públicamente en AutoMapa.</p>
        <Link to="/v/automap">Volver al inicio</Link>
      </main>
    );
  }

  const effectiveNoindex = shouldNoindexSeoPage(config, inventoryTotal);
  const displayMetadata = effectiveNoindex
    ? { ...metadata, robots: "noindex, follow", robotsDirective: "noindex, follow" }
    : metadata;

  return (
    <main className="seo-landing-page">
      <SeoLandingRenderer
        config={config}
        metadata={displayMetadata}
        breadcrumbs={breadcrumbs}
        internalLinks={internalLinks}
        faqItems={faqItems}
        listings={listings}
        featuredListings={featuredListings}
        inventoryTotal={inventoryTotal}
        loading={loading}
        inventoryError={inventoryError}
        dealers={dealers}
        dealersLoading={dealersLoading}
      />
    </main>
  );
}
